<template>
  <div v-if="!field.isHidden" :data-test="name" class="form-input-set" :class="{ 'decoration-negative': isError }">
    <label :for="'select_' + name" :style="isError ? 'margin-bottom: 0;' : null">
      <span v-html="$to(label)"></span>
    </label>
    <transition name="slide">
      <span
        v-if="isError"
        :id="'select_error_hint_' + name"
        style="display: block; margin-bottom: 3px; font-size: 18px;"
        class="text-negative"
        v-html="$to(internalHint)"
      ></span>
    </transition>
    <select
      :id="'select_' + name"
      ref="select"
      :value="internalValue"
      class="form-select"
      v-bind="$attrs"
      :disabled="isDisabled"
      :class="{ placeholder: hasActivePlaceholder }"
      :aria-required="required"
      :aria-invalid="isError ? true : null"
      :aria-describedby="isError ? 'select_error_hint_' + name : null"
      @change="checkValue"
    >
      <option v-if="placeholder" :value="internalValue ? '' : internalValue" :hidden="required" :selected="!internalValue">{{ $to(placeholder) }}</option>
      <slot>
        <option v-for="item in field.items" :key="item.id" :value="item.id" :selected="item.id === internalValue">{{ $to(item.label) }}</option>
      </slot>
    </select>
  </div>
</template>

<script>
import { emptyFormElement } from '@/utils/form';

export default {
  name: 'FormSelect',

  inheritAttrs: false,

  inject: {
    parentForm: {
      from: 'parentForm',
      default: null,
    },
  },

  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },

  props: {
    name: {
      type: String,
      required: true,
    },

    label: {
      type: String,
      required: false,
      default: null,
    },

    hint: {
      type: String,
      required: false,
      default: null,
    },

    modelValue: {
      type: [Number, String],
      required: false,
      default: '',
    },

    required: {
      type: Boolean,
      required: false,
      default: false,
    },

    form: {
      type: Object,
      required: false,
      default: null,
    },

    disabled: {
      type: Boolean,
      required: false,
    },

    placeholder: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      internalValue: this.modelValue,
      internalHint: null,
    };
  },

  computed: {
    state() {
      if (this.parentForm) return this.parentForm.form;
      return this.form;
    },

    field() {
      if (!this.state || !this.state[this.name]) return emptyFormElement();
      return this.state[this.name];
    },

    fieldHasFocus() {
      return this.field.hasFocus;
    },

    isError() {
      return !this.field.isValid && this.field.isChecked;
    },

    isDisabled() {
      return this.disabled || this.state.isDisabled;
    },

    hasActivePlaceholder() {
      return this.placeholder && this.internalValue === '';
    },
  },

  watch: {
    fieldHasFocus: 'checkFocus',
  },

  mounted() {
    if (!this.field.isHidden) {
      this.internalValue = this.field.value || this.internalValue;
      if (this.field.items && !this.field.items.some(item => item.id === this.internalValue)) {
        // given value not among provided options, select first (or placeholder, if present)
        this.internalValue = !this.placeholder && this.field.items.length ? this.field.items[0].id : '';
      }
      this.defaultValue = this.internalValue;
      this.field.checkValidation = this.checkValue;
      this.field.resetValue = this.resetValue;
      this.$set(this.field, 'name', this.name);
      this.$nextTick(() => {
        this.checkValue();
        this.checkFocus();
        this.field.isInit = true;
        if (this.field.promise) this.field.resolve();
      });
    }
  },

  methods: {
    checkFocus() {
      if (this.fieldHasFocus && this.$refs.select) {
        this.field.hasFocus = false;
        this.$refs.select.focus();
      }
    },

    checkValue() {
      this.internalValue = this.$refs.select.value;
      let isValid = true;
      if (this.required && !this.internalValue.length) {
        isValid = false;
      }
      this.internalHint = this.hint;
      // eslint-disable-next-line no-bitwise
      isValid &= !this.field.validators.some(v => {
        const error = !v.handler(this.internalValue, this.state);
        if (error && v.hint) this.internalHint = v.hint;
        return error;
      });
      this.$set(this.field, 'isValid', isValid);
      this.$set(this.field, 'value', this.internalValue);
      this.$emit('update:modelValue', this.internalValue);
    },

    resetValue() {
      this.$refs.select.value = this.defaultValue;
      this.checkValue();
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

.form-select {
  color: $color-gray-darker;

  &.placeholder {
    color: $color-gray;
  }
}
</style>
